import React, { useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "styles/theme";
import { Navbar } from "../../components/navbar";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Container,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Popover,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { t } from "i18n/translates";
import { fetchAgentData, fetchLocationData, LocationData } from "redux/dashboard/dashboard.slice";
import dayjs from "dayjs";
import { LineChart } from "./linar.chart";
import { formatDateTime } from "../../util/time";
import { getLocationActivityReport } from "./api";
import { Download, ShowChart } from "@mui/icons-material";
import { BlockLoader } from "../../components/loader";
import { get } from "lodash-es";
import { ExportModal } from "./export";
import { filterGridData } from "../../util/grid";

export const USER_STATE_OPTIONS = [
    { label: t("dashboard.agent_filters.active_yesterday"), value: "active_yesterday" },
    { label: t("dashboard.agent_filters.active_last_week"), value: "active_last_week" },
    { label: t("dashboard.agent_filters.active_last_month"), value: "active_last_month" },
    { label: t("dashboard.agent_filters.active_last_3_month"), value: "active_last_3_months" },
    { label: t("dashboard.agent_filters.inactive_yesterday"), value: "inactive_yesterday" },
    { label: t("dashboard.agent_filters.inactive_last_week"), value: "inactive_last_week" },
    { label: t("dashboard.agent_filters.inactive_last_month"), value: "inactive_last_month" },
    { label: t("dashboard.agent_filters.inactive_last_3_month"), value: "inactive_last_3_months" },
    { label: t("dashboard.agent_filters.never_logged_in"), value: "never_logged_in" },
];

function DashboardContent() {
    const dispatch = useAppDispatch();
    const { locationData, agentActivityPerDay, agentData, scorecardsData, loadingLocations, loadingAgents } =
        useAppSelector((state) => state.dashboard);

    const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
    const [agentFilterValues, setAgentFilterValues] = React.useState<string[]>([]);
    const [exportData, setExportData] = React.useState<{ name: string; data: string[][] } | null>(null);

    const apiRefLocations = useGridApiRef();
    const apiRefAgents = useGridApiRef();

    useEffect(() => {
        dispatch(fetchLocationData());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAgentData(agentFilterValues));
    }, [dispatch, agentFilterValues]);

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [locationPerDayActivityData, setLocationPerDayActivityData] = React.useState<
        { date: string; agents: number }[] | null
    >(null);

    const handleClickLocationPerDayActivity = (event: React.MouseEvent<HTMLButtonElement>, row: LocationData) => {
        setAnchorEl(event.currentTarget);
        setLocationPerDayActivityData(null);
        getLocationActivityReport(row.id)
            .then((data) => {
                if (data?.agent_activity_per_day && Object.keys(data.agent_activity_per_day).length > 0) {
                    setLocationPerDayActivityData(
                        Object.keys(data.agent_activity_per_day).map((d) => ({
                            date: dayjs(d).format("DD.MM"),
                            agents: data.agent_activity_per_day[d],
                        }))
                    );
                } else {
                    setLocationPerDayActivityData(
                        Array.from({ length: 7 }, (_, i) => {
                            return {
                                date: dayjs().subtract(i, "day").format("DD.MM"),
                                agents: 0,
                            };
                        })
                    );
                }
            })
            .catch(console.error);
    };

    const handleCloseLocationPerDayActivity = () => {
        setAnchorEl(null);
        setLocationPerDayActivityData(null);
    };

    const open = Boolean(anchorEl);

    const a11yProps = (index: number) => ({
        id: `tab-${index}`,
        "aria-controls": `tabpanel-${index}`,
        style: {
            fontSize: "16px",
            fontWeight: "bold",
        },
    });

    const renderStatCard = ({ cardKey }: { cardKey: string }) => {
        return (
            <Card
                key={cardKey}
                sx={{
                    flexGrow: "1",
                    flex: "1 1 0",
                }}
            >
                <CardContent
                    style={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "16px",
                        }}
                    >
                        {t(`dashboard.scorecards.${cardKey}` as any)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "46px",
                            fontWeight: "600",
                        }}
                    >
                        {(scorecardsData && scorecardsData[cardKey]) || 0}
                    </Typography>
                </CardContent>
            </Card>
        );
    };

    const locationColumns = [
        { field: "location", headerName: t("dashboard.location"), flex: 1 },
        { field: "tenant", headerName: t("dashboard.tenant"), flex: 1 },
        { field: "agency", headerName: t("dashboard.agency"), flex: 1 },
        { field: "agents_total", headerName: t("dashboard.total_agents"), flex: 1 },

        {
            field: "active_agents_yesterday",
            headerName: t("dashboard.active_agents_yesterday"),
            flex: 1,
        },
        {
            field: "agents_made_calls_yesterday",
            headerName: t("dashboard.made_calls_agents_yesterday"),
            flex: 1,
        },

        {
            field: "active_agents_last_week",
            headerName: t("dashboard.active_agents_1_week"),
            flex: 1,
        },
        {
            field: "agents_made_calls_last_week",
            headerName: t("dashboard.made_calls_agents_last_week"),
            flex: 1,
        },

        {
            field: "active_agents_last_month",
            headerName: t("dashboard.active_agents_1_month"),
            flex: 1,
        },
        {
            field: "agents_made_calls_last_month",
            headerName: t("dashboard.made_calls_agents_last_month"),
            flex: 1,
        },

        {
            field: "active_agents_last_3_months",
            headerName: t("dashboard.active_agents_3_months"),
            flex: 1,
        },
        {
            field: "agents_made_calls_last_3_months",
            headerName: t("dashboard.made_calls_agents_last_3_months"),
            flex: 1,
        },
    ];

    const agentColumns = [
        { field: "name", headerName: t("dashboard.agent_name"), flex: 1.2 },
        { field: "email", headerName: t("dashboard.agent_email"), flex: 1.2 },
        { field: "company_username", headerName: t("dashboard.agent_company_username"), flex: 1.2 },
        { field: "team", headerName: t("dashboard.team"), flex: 1 },
        { field: "location", headerName: t("dashboard.location"), flex: 1 },
        { field: "agency", headerName: t("dashboard.agency"), flex: 1 },
        { field: "tenant", headerName: t("dashboard.tenant"), flex: 1 },
        {
            field: "created",
            headerName: t("dashboard.created"),
            flex: 1,
            valueFormatter: (value: any) => (value ? formatDateTime(value) : "-"),
        },
        {
            field: "last_login",
            headerName: t("dashboard.last_login"),
            flex: 1,
            valueFormatter: (value: any) => (value ? formatDateTime(value) : "-"),
        },
        {
            field: "last_call",
            headerName: t("dashboard.last_call"),
            flex: 1,
            valueFormatter: (value: any) => (value ? formatDateTime(value) : "-"),
        },
    ];

    return (
        <ThemeProvider theme={theme}>
            <Navbar />
            <Container maxWidth={false} sx={{ pb: 3 }}>
                {loadingLocations ? (
                    <Box
                        sx={{
                            mt: 3,
                        }}
                    >
                        <BlockLoader />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            gap: 2,
                            mt: 3,
                        }}
                    >
                        <Card>
                            <CardContent
                                style={{
                                    height: "100%",
                                    padding: 0,
                                }}
                            >
                                <LineChart
                                    yLabel={t("dashboard.daily_active_agents")}
                                    data={Object.keys(agentActivityPerDay).map((d) => ({
                                        date: dayjs(d).format("DD.MM"),
                                        agents: agentActivityPerDay[d],
                                    }))}
                                />
                            </CardContent>
                        </Card>
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    gap: 2,
                                    flexGrow: "1",
                                    mb: 1,
                                    // flexWrap: "wrap",
                                }}
                            >
                                {[
                                    "active_agents_last_3_months",
                                    "active_agents_last_month",
                                    "active_agents_last_week",
                                    "active_agents_yesterday",
                                ].map((cardKey) => renderStatCard({ cardKey }))}
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    gap: 2,
                                    flexGrow: "1",
                                    mt: 1,
                                    // flexWrap: "wrap",
                                }}
                            >
                                {[
                                    "made_calls_agents_last_3_months",
                                    "made_calls_agents_last_month",
                                    "made_calls_agents_last_week",
                                    "made_calls_agents_yesterday",
                                ].map((cardKey) => renderStatCard({ cardKey }))}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                minWidth: "130px",
                            }}
                        >
                            {renderStatCard({ cardKey: "total_agents" })}
                        </Box>
                    </Box>
                )}

                <Box sx={{ mt: 3, mb: 1, display: "flex", justifyContent: "space-between" }}>
                    <Tabs
                        value={selectedTabIndex}
                        onChange={(event: React.SyntheticEvent, newValue: number) => {
                            setSelectedTabIndex(newValue);
                        }}
                        aria-label="basic tabs example"
                    >
                        <Tab label={t("dashboard.location_view")} {...a11yProps(0)} />
                        <Tab label={t("dashboard.agent_list")} {...a11yProps(1)} />
                    </Tabs>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {selectedTabIndex === 1 && (
                            <div>
                                <FormControl size="small" sx={{ m: 1, minWidth: 300 }}>
                                    <InputLabel id="agent_filters_label">
                                        {t(`dashboard.agent_filters_title`)}
                                    </InputLabel>
                                    <Select
                                        label={t(`dashboard.agent_filters_title`)}
                                        labelId="agent_filters_label"
                                        id="agent_filters"
                                        multiple
                                        value={agentFilterValues}
                                        onChange={(event: SelectChangeEvent<string[]>) => {
                                            const {
                                                target: { value },
                                            } = event;

                                            setAgentFilterValues(typeof value === "string" ? value.split(",") : value);
                                        }}
                                        renderValue={(selected) =>
                                            selected
                                                .map((s) => {
                                                    return USER_STATE_OPTIONS.find((v) => v.value === s)?.label;
                                                })
                                                .join(", ")
                                        }
                                        input={<OutlinedInput label={t(`dashboard.agent_filters_title`)} />}
                                    >
                                        {USER_STATE_OPTIONS.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                                // style={(name: string, personName: string[], theme: Theme) => {
                                                //     return {
                                                //         fontWeight: personName.includes(name)
                                                //             ? theme.typography.fontWeightMedium
                                                //             : theme.typography.fontWeightRegular,
                                                //     };
                                                // }}
                                            >
                                                <Checkbox checked={agentFilterValues.includes(option.value)} />
                                                <ListItemText primary={option.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        )}

                        <Box
                            sx={{
                                ml: 2,
                            }}
                        >
                            <Button
                                onClick={() => {
                                    switch (selectedTabIndex) {
                                        case 0: {
                                            // locations
                                            const currentGridFilter =
                                                apiRefLocations.current?.exportState()?.filter?.filterModel?.items ||
                                                [];

                                            setExportData({
                                                data: [
                                                    locationColumns.map((col) => col.headerName),
                                                    ...locationData
                                                        .filter(filterGridData(currentGridFilter))
                                                        .map((row) => {
                                                            return locationColumns.map(
                                                                (col) => get(row, col.field) || ""
                                                            );
                                                        }),
                                                ],
                                                name: "locations_list",
                                            });
                                            break;
                                        }
                                        case 1: {
                                            // agents
                                            const currentGridFilter =
                                                apiRefAgents.current?.exportState()?.filter?.filterModel?.items || [];

                                            setExportData({
                                                data: [
                                                    agentColumns.map((col) => col.headerName),
                                                    ...agentData
                                                        .filter(filterGridData(currentGridFilter))
                                                        .map((row) => {
                                                            return agentColumns.map((col) => {
                                                                const val = get(row, col.field) || "";
                                                                if (col.valueFormatter) {
                                                                    return col.valueFormatter(val);
                                                                }
                                                                return val;
                                                            });
                                                        }),
                                                ],
                                                name: "agent_list",
                                            });
                                            break;
                                        }
                                    }
                                }}
                                startIcon={<Download />}
                            >
                                {t("dashboard.export_data")}
                            </Button>
                        </Box>
                    </Box>
                </Box>
                {selectedTabIndex === 0 && (
                    <DataGrid
                        apiRef={apiRefLocations}
                        disableRowSelectionOnClick
                        rows={locationData}
                        columns={[
                            ...locationColumns,
                            {
                                field: "daily_active_agents",
                                headerName: t("dashboard.daily_active_agents"),
                                renderCell: ({ row }) => {
                                    return (
                                        <div>
                                            <Button
                                                variant="contained"
                                                onClick={(e) => handleClickLocationPerDayActivity(e, row)}
                                            >
                                                <ShowChart />
                                            </Button>
                                        </div>
                                    );
                                },
                            },
                        ]}
                        loading={loadingLocations}
                        getRowId={(row) => `${row.location}-${row.agency}-${row.tenant}`}
                        pageSizeOptions={[10, 50, 100, { value: -1, label: "All" }]}
                    />
                )}
                {selectedTabIndex === 1 && (
                    <DataGrid
                        apiRef={apiRefAgents}
                        disableRowSelectionOnClick
                        rows={agentData}
                        columns={agentColumns}
                        loading={loadingAgents}
                        getRowId={(row) => row.id}
                        pageSizeOptions={[10, 50, 100, { value: -1, label: "All" }]}
                    />
                )}

                {anchorEl && (
                    <Popover // location per day activity popover
                        id={"location-per-day-activity-popover"}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCloseLocationPerDayActivity}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        {!locationPerDayActivityData && (
                            <Box sx={{ width: 600, height: 300 }}>
                                <BlockLoader />
                            </Box>
                        )}
                        {locationPerDayActivityData && (
                            <LineChart yLabel={t("dashboard.daily_active_agents")} data={locationPerDayActivityData} />
                        )}
                    </Popover>
                )}
                <ExportModal
                    onClose={() => setExportData(null)}
                    tableData={exportData?.data}
                    fileName={exportData?.name}
                />
            </Container>
        </ThemeProvider>
    );
}

export default function DashboardPage() {
    return <DashboardContent />;
}
